import {makeAutoObservable} from 'mobx';
import {
    DISTRIBUTION_END_AGE,
    DISTRIBUTION_START_AGE,
    PROSPER_DEFAULT_RATE_OF_RETURN,
} from '../../../Agent/Quotes/constants';
import {Parameter} from './Parameter';
import {fieldOptions} from './QuoteXref';
import Utils from '../../Utils';

class QuoteParameter extends Parameter {
    valueAsString = () => {
        return fieldOptions.hasOwnProperty(this.id)
            ? (fieldOptions[this.id as keyof typeof fieldOptions].options
                  .find((o) => Utils.MakeVarName(o) === this.value)
                  ?.toString() ?? this.value.toString())
            : this.value.toString();
    };
}

export class Quote {
    isCombination: boolean = false;

    productType: Parameter = new QuoteParameter('productType', 'IUL');
    productIUL: Parameter = new QuoteParameter('productIUL', 'FGEverlast');
    productTerm: Parameter = new QuoteParameter('productTerm', 'AmeritasFLXLivingBenefitsTerm');
    productGUL: Parameter = new QuoteParameter('productGUL', 'NorthAmericanCustomGuaranteeGen9');
    productGuaranteed: Parameter = new QuoteParameter('productGuaranteed');
    productWHL: Parameter = new QuoteParameter('productWHL', 'MOOWholeLife');
    productVUL: Parameter = new QuoteParameter('productVUL', 'AmeritasAdvisorIIVUL');
    lswFlexLifeNL: Parameter = new QuoteParameter('lswFlexLifeNL', false);
    lswFlexLife: Parameter = new QuoteParameter('lswFlexLife', false);
    healthRatingLSWIUL: Parameter = new QuoteParameter('healthRatingLSWIUL', 'StandardNonTobacco');
    healthRatingAmeritasJuvenile: Parameter = new QuoteParameter('healthRatingAmeritasJuvenile', 'Juvenile');
    healthRatingAmeritasJuvenileVUL: Parameter = new QuoteParameter('healthRatingAmeritasJuvenileVUL', 'NonTobacco');
    healthRatingAmeritasAdult: Parameter = new QuoteParameter('healthRatingAmeritasAdult', 'NonTobacco');
    healthRatingAmeritasAdultVUL: Parameter = new QuoteParameter('healthRatingAmeritasAdultVUL', 'NonTobacco');
    healthRatingMOOIUL: Parameter = new QuoteParameter('healthRatingMOOIUL', 'StandardNonTobacco');
    healthRatingMOOWHL: Parameter = new QuoteParameter('healthRatingMOOWHL', 'StandardNonTobacco');
    healthRatingTAFE: Parameter = new QuoteParameter('healthRatingTAFE', 'NonSmoker');
    healthRatingFGIUL: Parameter = new QuoteParameter('healthRatingFGIUL', 'StandardNonsmoker');
    tableRatingFGIUL: Parameter = new QuoteParameter('tableRatingFGIUL', 'None');
    healthRatingAmeritasTerm: Parameter = new QuoteParameter('healthRatingAmeritasTerm', 'Standard');
    smokerRatingAmeritasTerm: Parameter = new QuoteParameter('smokerRatingAmeritasTerm', 'NonTobacco');
    smokerRatingAssurityTerm: Parameter = new QuoteParameter('smokerRatingAssurityTerm', 'NonTobacco');
    healthRatingLSW: Parameter = new QuoteParameter('healthRatingLSW', 'Standard');
    healthRatingAssurityTerm: Parameter = new QuoteParameter('healthRatingAssurityTerm', 'Standard');
    smokerRatingLSW: Parameter = new QuoteParameter('smokerRatingLSW', 'NonTobacco');
    healthRatingNA: Parameter = new QuoteParameter('healthRatingNA', 'Standard');
    smokerRatingNA: Parameter = new QuoteParameter('smokerRatingNA', 'NonTobacco');
    healthRatingMOO: Parameter = new QuoteParameter('healthRatingMOO', 'Level');
    smokerRatingMOO: Parameter = new QuoteParameter('smokerRatingMOO', 'NonTobacco');
    tableRatingAmeritas: Parameter = new QuoteParameter('tableRatingAmeritas', 'None');
    tableRatingLSWIUL: Parameter = new QuoteParameter('tableRatingLSWIUL', 'None');
    tableRatingLSWIUL2: Parameter = new QuoteParameter('tableRatingLSWIUL2', 'None');
    tableRatingAmeritasTerm: Parameter = new QuoteParameter('tableRatingAmeritasTerm', 'None');
    tableRatingAmeritasVUL: Parameter = new QuoteParameter('tableRatingAmeritasVUL', 'None');
    tableRatingLSW: Parameter = new QuoteParameter('tableRatingLSW', 'None');
    tableRatingNA: Parameter = new QuoteParameter('tableRatingNA', 'None');
    flatExtra: Parameter = new QuoteParameter('flatExtra', false);
    prosperAmplifySolves: Parameter = new QuoteParameter('prosperAmplifySolves');
    prosperSpecify: Parameter = new QuoteParameter('prosperSpecify');
    prosperAmount: Parameter = new QuoteParameter('prosperAmount');
    prosperSmoker: Parameter = new QuoteParameter('prosperSmoker');
    prosperRiskClass: Parameter = new QuoteParameter('prosperRiskClass', 'StandardNonTobacco');
    prosperTableRating: Parameter = new QuoteParameter('prosperTableRating', 'NotRated');
    prosperFlatExtra: Parameter = new QuoteParameter('prosperFlatExtra', 0);
    prosperFlatExtraDuration: Parameter = new QuoteParameter('prosperFlatExtraDuration', 'M');
    prosperFaceAmountType: Parameter = new QuoteParameter('prosperFaceAmountType', 'Target');
    prosperFaceAmount: Parameter = new QuoteParameter('prosperFaceAmount');
    prosperDeathBenefitOption: Parameter = new QuoteParameter('prosperDeathBenefitOption', 'BIncreasing');
    prosperDeathBenefitChangeAge: Parameter = new QuoteParameter('prosperDeathBenefitChangeAge', 66);
    prosperPremiumType: Parameter = new QuoteParameter('prosperPremiumType', 'Specify');
    prosperPremium: Parameter = new QuoteParameter('prosperPremium', 0);
    prosperPremiumMode: Parameter = new QuoteParameter('prosperPremiumMode', 'Monthly');
    prosperLumpSum: Parameter = new QuoteParameter('prosperLumpSum', 0);
    prosperPremiumEndYear: Parameter = new QuoteParameter('prosperPremiumEndYear');
    prosperPremiumAge: Parameter = new QuoteParameter('prosperPremiumAge');
    prosperIndexName: Parameter = new QuoteParameter('prosperIndexName', 'S&P 500');
    prosperIndexedAllocationPercent: Parameter = new QuoteParameter('prosperIndexedAllocationPercent', 100);
    prosperIndexedAccountRateOfReturn: Parameter = new QuoteParameter(
        'prosperIndexedAccountRateOfReturn',
        PROSPER_DEFAULT_RATE_OF_RETURN,
    );
    prosperDistributionSolveType: Parameter = new QuoteParameter('prosperDistributionSolveType', 'Specify');
    prosperDistributionAmount: Parameter = new QuoteParameter('prosperDistributionAmount');
    prosperDistributionType: Parameter = new QuoteParameter('prosperDistributionType', 'IndexedLoan');
    prosperDistributionStartAge: Parameter = new QuoteParameter('prosperDistributionStartAge', DISTRIBUTION_START_AGE);
    prosperDistributionEndAge: Parameter = new QuoteParameter('prosperDistributionEndAge', DISTRIBUTION_END_AGE);
    prosperAllowMEC: Parameter = new QuoteParameter('prosperAllowMEC', false);
    prosperDefinitionOfLifeInsurance: Parameter = new QuoteParameter('prosperDefinitionOfLifeInsurance', 'CVAT');
    prosperSolveGoal: Parameter = new QuoteParameter('prosperSolveGoal', 0);
    prosperSolveAge: Parameter = new QuoteParameter('prosperSolveAge', 100);
    prosperIllustrationRequest: Parameter = new QuoteParameter('prosperIllustrationRequest', true);
    prosperEffectiveDate: Parameter = new QuoteParameter('prosperEffectiveDate', new Date());

    flatExtraPerm: Parameter = new QuoteParameter('flatExtraPerm', 0);
    flatExtraYears: Parameter = new QuoteParameter('flatExtraYears', 0);
    flatExtraTemp: Parameter = new QuoteParameter('flatExtraTemp', 0);
    solveScenario: Parameter = new QuoteParameter('solveScenario', 'Premiumknowncoverage');
    solveDetailsPremium: Parameter = new QuoteParameter('solveDetailsPremium', 'Solvefortargetpremium');
    solveDetailsCoverage: Parameter = new QuoteParameter('solveDetailsCoverage', 'Solvefortargetcoverage');
    specifiedFaceAmount: Parameter = new QuoteParameter('specifiedFaceAmount');
    specifiedFaceAmountLSW: Parameter = new QuoteParameter('specifiedFaceAmountLSW', '100000');
    specifiedFaceAmountWinflex: Parameter = new QuoteParameter('specifiedFaceAmountWinflex');
    specifiedFaceAmountMOO: Parameter = new QuoteParameter('specifiedFaceAmountMOO');
    specifiedFaceAmountTransGerber: Parameter = new QuoteParameter('specifiedFaceAmountTransGerber');
    specifiedFaceAmountGW: Parameter = new QuoteParameter('specifiedFaceAmountGW');
    complianceTest: Parameter = new QuoteParameter('complianceTest', 'GuidelinePremiumTest');
    complianceTestLSWIUL: Parameter = new QuoteParameter('complianceTestLSWIUL', 'Cash_Value_Accumulation_Test');
    complianceTestVUL: Parameter = new QuoteParameter('complianceTestVUL', 'CashValueAccumulationTest');
    mecAvoidance: Parameter = new QuoteParameter('mecAvoidance', true);
    mecAvoidanceLSWIUL: Parameter = new QuoteParameter('mecAvoidanceLSWIUL', 'None');
    deathBenefitOption: Parameter = new QuoteParameter('deathBenefitOption', 'Increasing');
    deathBenefitOptionMOOIUL: Parameter = new QuoteParameter('deathBenefitOptionMOOIUL', 'Level');
    deathBenefitOptionFGPathsetter: Parameter = new QuoteParameter('deathBenefitOptionFGPathsetter', 'Increasing');
    deathBenefitChangeYear: Parameter = new QuoteParameter('deathBenefitChangeYear', 70);
    deathBenefitChangeYearFGPathsetter: Parameter = new QuoteParameter('deathBenefitChangeYearFGPathsetter', 66);
    solveForIUL: Parameter = new QuoteParameter('solveForIUL', 'Nosolve');
    solveForIULMoo: Parameter = new QuoteParameter('solveForIULMoo', 'EasySolve');
    solveForTAFE: Parameter = new QuoteParameter('solveForTAFE', 'Premium');
    solveForIULFGPathsetter: Parameter = new QuoteParameter('solveForIULFGPathsetter', 'FaceSolve');
    easySolveOptionIULMoo: Parameter = new QuoteParameter('easySolveOptionIULMoo', 'Premium');
    solveForWHLMoo: Parameter = new QuoteParameter('solveForWHLMoo', 'EasySolve');
    easySolveOptionWHLMoo: Parameter = new QuoteParameter('easySolveOptionWHLMoo', 'Premium');
    deathBenefitOptionLSWIUL: Parameter = new QuoteParameter('deathBenefitOptionLSWIUL', 'B_Increasing');
    deathBenefitChangeAgeLSW: Parameter = new QuoteParameter('deathBenefitChangeAgeLSW', 66);
    solveForIULLSW: Parameter = new QuoteParameter('solveForIULLSW', 'Premium');
    solveTargetLSWIUL: Parameter = new QuoteParameter('solveTargetLSWIUL', 'Net_Surrender_Value');
    firstYearLumpSumLSWIUL: Parameter = new QuoteParameter('firstYearLumpSumLSWIUL', 0);
    deductFirstModalPremiumFromLumpSum: Parameter = new QuoteParameter('deductFirstModalPremiumFromLumpSum', false);
    solveForGUL: Parameter = new QuoteParameter('solveForGUL', 'Guaranteedpremium');
    faceAmount: Parameter = new QuoteParameter('faceAmount', 'Specified');
    deathBenefitGuaranteeAge: Parameter = new QuoteParameter('deathBenefitGuaranteeAge', 'To age 121 (No Lapse U/L)');
    termPeriod: Parameter = new QuoteParameter('termPeriod');
    termPeriodAssurity: Parameter = new QuoteParameter('termPeriodAssurity');
    faceSolveOption: Parameter = new QuoteParameter('faceSolveOption', 'Cashvaluetarget');
    faceSolveOptionFGPathsetter: Parameter = new QuoteParameter('faceSolveOptionFGPathsetter', 'Target');
    premiumMode: Parameter = new QuoteParameter('premiumMode', 'Target');
    premiumModeLSW: Parameter = new QuoteParameter('premiumModeLSW', 'Annual');
    specifiedPremium: Parameter = new QuoteParameter('specifiedPremium');
    specifiedPremiumTAFE: Parameter = new QuoteParameter('specifiedPremiumTAFE');
    specifiedFaceAmountTAFE: Parameter = new QuoteParameter('specifiedFaceAmountTAFE', '10000');
    premiumFrequencyTAFE: Parameter = new QuoteParameter('premiumFrequencyTAFE', 'Monthly');
    specifiedPremiumFGPathsetter: Parameter = new QuoteParameter('specifiedPremiumFGPathsetter');
    premiumSolveOption: Parameter = new QuoteParameter('premiumSolveOption', 'Cashvaluetarget');
    premiumSolveOptionLSW: Parameter = new QuoteParameter('premiumSolveOptionLSW', 'Target');
    premiumSolveOptionFGPathsetter: Parameter = new QuoteParameter('premiumSolveOptionFGPathsetter', 'Target');
    premiumEndAgeAmeritas: Parameter = new QuoteParameter('premiumEndAgeAmeritas', 'specifiedage');
    specifiedPremiumEndAgeAmeritas: Parameter = new QuoteParameter('specifiedPremiumEndAgeAmeritas', '121');
    specifiedPremiumEndAgeFGPathsetter: Parameter = new QuoteParameter('specifiedPremiumEndAgeFGPathsetter', '121');
    lumpSumFGPathSetter: Parameter = new QuoteParameter('lumpSumFGPathSetter', '0');
    cashValueTargetAge: Parameter = new QuoteParameter('cashValueTargetAge');
    cashValueTargetAmount: Parameter = new QuoteParameter('cashValueTargetAmount');
    cashFlowStartAge: Parameter = new QuoteParameter('cashFlowStartAge');
    cashFlowEndAge: Parameter = new QuoteParameter('cashFlowEndAge');
    cashFlowSolveOption: Parameter = new QuoteParameter('cashFlowSolveOption', 'Paidupendorsement');
    premiumFrequency: Parameter = new QuoteParameter('premiumFrequency', 'monthly');
    premiumFrequencyMOOIUL: Parameter = new QuoteParameter('premiumFrequencyMOOIUL', 'Monthly');
    premiumFrequencyFGPathsetter: Parameter = new QuoteParameter('premiumFrequencyFGPathsetter', 'Monthly');
    lswPremiumSolveType1: Parameter = new QuoteParameter('lswPremiumSolveType1', 'Target_Premium');
    lswPremiumAmount1: Parameter = new QuoteParameter('lswPremiumAmount1');
    lswPremiumStart1: Parameter = new QuoteParameter('lswPremiumStart1', '1');
    lswPremiumEnd1: Parameter = new QuoteParameter('lswPremiumEnd1', 'M');
    lswPremiumSolveType2: Parameter = new QuoteParameter('lswPremiumSolveType2', 'Target_Premium');
    lswPremiumAmount2: Parameter = new QuoteParameter('lswPremiumAmount2');
    lswPremiumStart2: Parameter = new QuoteParameter('lswPremiumStart2');
    lswPremiumEnd2: Parameter = new QuoteParameter('lswPremiumEnd2');
    lswPremiumSolveType3: Parameter = new QuoteParameter('lswPremiumSolveType3', 'Target_Premium');
    lswPremiumAmount3: Parameter = new QuoteParameter('lswPremiumAmount3');
    lswPremiumStart3: Parameter = new QuoteParameter('lswPremiumStart3');
    lswPremiumEnd3: Parameter = new QuoteParameter('lswPremiumEnd3');
    lswPremiumSolveType4: Parameter = new QuoteParameter('lswPremiumSolveType4', 'Target_Premium');
    lswPremiumAmount4: Parameter = new QuoteParameter('lswPremiumAmount4');
    lswPremiumStart4: Parameter = new QuoteParameter('lswPremiumStart4');
    lswPremiumEnd4: Parameter = new QuoteParameter('lswPremiumEnd4');
    lswPremiumSolveType5: Parameter = new QuoteParameter('lswPremiumSolveType5', 'Target_Premium');
    lswPremiumAmount5: Parameter = new QuoteParameter('lswPremiumAmount5');
    lswPremiumStart5: Parameter = new QuoteParameter('lswPremiumStart5');
    lswPremiumEnd5: Parameter = new QuoteParameter('lswPremiumEnd5');
    disbursement: Parameter = new QuoteParameter('disbursement', false);
    incomeOption: Parameter = new QuoteParameter('incomeOption', 'Withdrawals');
    incomeStartAge: Parameter = new QuoteParameter('incomeStartAge');
    incomeEndAge: Parameter = new QuoteParameter('incomeEndAge');
    annualIncomeAmount: Parameter = new QuoteParameter('annualIncomeAmount');
    overloanProtection: Parameter = new QuoteParameter('overloanProtection', true);
    fixedInterest: Parameter = new QuoteParameter('fixedInterest');
    sp1yr: Parameter = new QuoteParameter('sp1yr', 50);
    spAdjust: Parameter = new QuoteParameter('spAdjust');
    sp2yr: Parameter = new QuoteParameter('sp2yr');
    russell: Parameter = new QuoteParameter('russell');
    bnp1yr: Parameter = new QuoteParameter('bnp1yr', 50);
    bnp2yr: Parameter = new QuoteParameter('bnp2yr');
    fundPercentValidation: Parameter = new QuoteParameter('fundPercentValidation', "Percentages don't add up to 100%");
    acceleratedBenefit: Parameter = new QuoteParameter('acceleratedBenefit', true);
    accidentalBenefit: Parameter = new QuoteParameter('accidentalBenefit', false);
    accidentalBenefitAmount: Parameter = new QuoteParameter('accidentalBenefitAmount', 50000);
    accidentalBenefitRating: Parameter = new QuoteParameter('accidentalBenefitRating');
    accidentalBenefitToAge: Parameter = new QuoteParameter('accidentalBenefitToAge', 70);
    childrensRider: Parameter = new QuoteParameter('childrensRider', false);
    childrensRiderAmount: Parameter = new QuoteParameter('childrensRiderAmount', 25000);
    childrensRiderToAge: Parameter = new QuoteParameter('childrensRiderToAge', 70);
    lifetimeIncomeRider: Parameter = new QuoteParameter('lifetimeIncomeRider', false);
    lifetimeIncomeType: Parameter = new QuoteParameter('lifetimeIncomeType', 'Level');
    lifetimeIncomeStartAge: Parameter = new QuoteParameter('lifetimeIncomeStartAge', 50);
    solveScenarioVUL: Parameter = new QuoteParameter('solveScenarioVUL', 'Setpremiumsolveforminimumnonmecfaceamount');
    premiumAmountVUL: Parameter = new QuoteParameter('premiumAmountVUL');
    faceAmountVUL: Parameter = new QuoteParameter('faceAmountVUL');
    premiumPayToAgeVUL: Parameter = new QuoteParameter('premiumPayToAgeVUL', '121');
    mainRateVUL: Parameter = new QuoteParameter('mainRateVUL', 9);
    deathBenefitOptionVUL: Parameter = new QuoteParameter('deathBenefitOptionVUL', 'Increasing');
    deathBenefitChangeAgeVUL: Parameter = new QuoteParameter('deathBenefitChangeAgeVUL', '70');
    childrensRiderTermSelector: Parameter = new QuoteParameter('childrensRiderTermSelector', false);
    childRiderUnitsTerm: Parameter = new QuoteParameter('childRiderUnitsTerm', '0');
    DeathBenefitProtection: Parameter = new QuoteParameter('DeathBenefitProtection', false);
    deathBenefitProtection: Parameter = new QuoteParameter('deathBenefitProtection', true);
    ABRTerminalIllness: Parameter = new QuoteParameter('ABRTerminalIllness', true);
    ABRChronicIllness: Parameter = new QuoteParameter('ABRChronicIllness', true);
    ABRCriticalIllness: Parameter = new QuoteParameter('ABRCriticalIllness', true);
    ABRCriticalInjury: Parameter = new QuoteParameter('ABRCriticalInjury', true);
    ABRSummaryAge: Parameter = new QuoteParameter('ABRSummaryAge', 65);
    includeReportLsw: Parameter = new QuoteParameter('includeReportLsw', true);
    SP500AllocationPercentage: Parameter = new QuoteParameter('SP500AllocationPercentage', 100);
    USPacesetterAllocationPercentage: Parameter = new QuoteParameter('USPacesetterAllocationPercentage', 0);
    ChildrensTerm: Parameter = new QuoteParameter('ChildrensTerm', false);
    WaiverofPremium: Parameter = new QuoteParameter('WaiverofPremium');
    WaiverofPremiumOption: Parameter = new QuoteParameter('WaiverofPremiumOption');
    specifiedAmount: Parameter = new QuoteParameter('specifiedAmount', 0);
    productADB: Parameter = new QuoteParameter('productADB');
    planTypeADB: Parameter = new QuoteParameter('planTypeADB', '_1Adult');
    specifiedCoverageADB: Parameter = new QuoteParameter('specifiedCoverageADB', '250000');
    secondCoveredAdultAgeADB: Parameter = new QuoteParameter('secondCoveredAdultAgeADB');
    includeChildrenADB: Parameter = new QuoteParameter('includeChildrenADB', false);
    returnOnPremiumADB: Parameter = new QuoteParameter('returnOnPremiumADB');
    productAnnuity: Parameter = new QuoteParameter('productAnnuity', 'FlexSecureGrowth10');
    monthlyPremiumAnnuity: Parameter = new QuoteParameter('monthlyPremiumAnnuity', '100');
    singlePremiumAnnuity: Parameter = new QuoteParameter('singlePremiumAnnuity', '0');
    allocationAnnuity: Parameter = new QuoteParameter('allocationAnnuity', 'SP500PointtoPoint');

    get ameritas() {
        const param: Parameter = new QuoteParameter('ameritas');
        param.value =
            (this.productIUL.value == 'AmeritasIULFLXlivingbenefits' && this.productType.value == 'IUL') ||
            (this.productTerm.value == 'AmeritasFLXLivingBenefitsTerm' && this.productType.value == 'Term') ||
            (this.productVUL.value == 'AmeritasAdvisorIIVUL' && this.productType.value == 'VUL');
        return param;
    }

    get northAmerican() {
        const param: Parameter = new QuoteParameter('northAmerican');
        param.value =
            (this.productTerm.value == 'NorthAmericanTermADDvantage' && this.productType.value == 'Term') ||
            (this.productGUL.value == 'NorthAmericanCustomGuaranteeGen9' && this.productType.value == 'GUL');
        return param;
    }

    get lsw() {
        const param: Parameter = new QuoteParameter('lsw');
        param.value = this.productTerm.value == 'NationalLifeGroupLSWTerm' && this.productType.value == 'Term';
        return param;
    }

    get assurityLife() {
        const param: Parameter = new QuoteParameter('assurityLife');
        param.value =
            this.productTerm.value == 'AssurityLifeInsuranceCompanyTermLifeInsurance' &&
            this.productType.value == 'Term';
        return param;
    }

    get moo() {
        const param: Parameter = new QuoteParameter('moo');
        param.value =
            (this.productGuaranteed.value == 'MutualofOmahaLivingPromise' && this.productType.value == 'Guaranteed') ||
            (this.productIUL.value == 'MOOIULExpress' && this.productType.value == 'IUL') ||
            (this.productWHL.value == 'MOOWholeLife' && this.productType.value == 'Whole Life');

        return param;
    }

    get fg() {
        const param: Parameter = new QuoteParameter('fg');
        param.value = ['FGPathsetter', 'FGEverlast'].includes(this.productIUL.value) && this.productType.value == 'IUL';
        return param;
    }

    constructor() {
        makeAutoObservable(this);
    }

    static isRequiredField(key: string) {
        return [
            'productGuaranteed',
            'solveScenario',
            'solveDetailsPremium',
            'solveDetailsCoverage',
            'specifiedFaceAmount',
            'specifiedFaceAmountWinflex',
            'specifiedFaceAmountMOO',
            'specifiedFaceAmountTransGerber',
            'specifiedFaceAmountGW',
            'termPeriod',
            'termPeriodAssurity',
            'specifiedPremium',
            'solveScenarioVUL',
            'premiumAmountVUL',
            'faceAmountVUL',
            'premiumPayToAgeVUL',
            'mainRateVUL',
            'deathBenefitOptionVUL',
            'deathBenefitChangeAgeVUL',
            'childRiderUnitsTerm',
            'includeReportLsw',
            'prosperRiskClass',
            'prosperFaceAmount',
            'prosperIllustrationRequest',
            'prosperPremium',
        ].includes(key);
    }
}
